import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, FileText, Pencil } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useCMS } from '../context/CMSContext';
import { useAuth } from '../context/AuthContext';
import { ProductImage } from './VialIllustration';

// ----- Subcomponents (kept tiny so the top-level card stays low-complexity) -----

const StockBadge = () => (
  <div className="absolute top-3 left-3 bg-slate-800 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full">Restocking Soon</div>
);

const PurityRibbon = () => (
  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
    <div className="flex items-center gap-1.5 bg-white border border-red-200 text-red-700 text-[11px] font-semibold px-2.5 py-1 rounded-full shadow-sm">
      <FileText size={12} />Purity Report
    </div>
  </div>
);

const EditPill = () => (
  <Link to="/admin" onClick={(e) => e.stopPropagation()}
    className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2 py-1 bg-red-600 text-white text-[11px] font-semibold rounded-md shadow">
    <Pencil size={11} /> Edit
  </Link>
);

const ProductMedia = ({ product, editable }) => (
  <div className="relative aspect-square bg-gradient-to-b from-slate-50 to-white overflow-hidden flex items-center justify-center">
    <ProductImage
      product={product}
      className="w-full h-full object-contain p-6 transition-transform duration-500 group-hover:scale-105"
      containerClassName="w-full h-full p-6 flex items-center justify-center transition-transform duration-500 group-hover:scale-105"
    />
    {!product.in_stock && <StockBadge />}
    <PurityRibbon />
    {editable && <EditPill />}
  </div>
);

const AddToCartButton = ({ product, editable, onAdd }) => {
  const buyable = product.in_stock && !editable;
  const label = product.in_stock ? `Add — $${Number(product.price).toFixed(2)}` : 'Restocking Soon';
  return (
    <button
      onClick={onAdd}
      disabled={!buyable}
      className={`mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
        buyable ? 'bg-red-500 hover:bg-red-400 text-white' : 'bg-slate-100 text-slate-400 cursor-not-allowed'
      }`}
    >
      <ShoppingCart size={16} strokeWidth={2} />
      {label}
    </button>
  );
};

// ----- Main card -----

const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const { editMode, updateProduct } = useCMS();
  const { user } = useAuth();
  const editable = !!user && editMode;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!product.in_stock) return;
    addItem({ ...product, qty: 1 });
  };

  const onNameBlur = async (e) => {
    const v = e.target.innerText.trim();
    if (v && v !== product.name) await updateProduct(product.id, { name: v });
  };

  const onPriceBlur = async (e) => {
    const raw = e.target.innerText.replace(/[^0-9.]/g, '');
    const v = parseFloat(raw);
    if (!isNaN(v) && v !== product.price) await updateProduct(product.id, { price: v });
  };

  return (
    <div className="group relative flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-red-300 hover:shadow-lg hover:shadow-red-900/5 transition-all duration-300">
      <Link to={editable ? '#' : `/product/${product.slug}`} onClick={(e) => editable && e.preventDefault()} className="contents">
        <ProductMedia product={product} editable={editable} />
      </Link>

      <div className="p-5 flex flex-col gap-2.5 border-t border-slate-100">
        <h3
          className={`font-semibold text-[#0a0a0a] text-[15px] leading-snug ${editable ? 'cms-editable' : 'line-clamp-1'}`}
          contentEditable={editable}
          suppressContentEditableWarning
          onBlur={onNameBlur}
        >
          {product.name}
        </h3>
        <span className="inline-block w-fit text-[11px] font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded-full">{product.purity}</span>
        <div
          className={`text-red-700 font-bold text-lg ${editable ? 'cms-editable' : ''}`}
          contentEditable={editable}
          suppressContentEditableWarning
          onBlur={onPriceBlur}
        >
          ${Number(product.price).toFixed(2)}
        </div>
        <AddToCartButton product={product} editable={editable} onAdd={handleAdd} />
      </div>
    </div>
  );
};

export default ProductCard;
